import { FC, useState } from "react";
import { CheckCircle, Wrench, AlertTriangle, Info, Activity, Trash2, Loader2 } from "lucide-react";
import { MEDIA_TYPES } from "@/config/media";
import { NoteImage } from "./NoteImage";
import { NoteVideo } from "./NoteVideo";
import { NoteFile } from "./NoteFile";
import { NoteUnsupported } from "./NoteUnsupported";
import { format } from "date-fns";

interface NoteProps {
	note: Dryp.Note;
	canDelete?: boolean;
	onDelete?: (noteId: Dryp.NoteId) => Promise<void>;
}

const NOTE_STYLES: Record<string, { icon: React.ElementType; color: string }> = {
	inspection: { icon: CheckCircle, color: "bg-emerald-500" },
	maintenance: { icon: Wrench, color: "bg-blue-500" },
	alert: { icon: AlertTriangle, color: "bg-amber-500" },
	info: { icon: Info, color: "bg-gray-500" },
	activity: { icon: Activity, color: "bg-purple-500" },
};

const getNoteStyle = (note: Dryp.Note) => {
	const text = note.description?.toLowerCase() || "";
	if (text.includes("inspection") || text.includes("completed")) return NOTE_STYLES.inspection;
	if (text.includes("maintenance") || text.includes("cleared") || text.includes("repair"))
		return NOTE_STYLES.maintenance;
	if (text.includes("alert") || text.includes("high") || text.includes("exceeded")) return NOTE_STYLES.alert;
	if (text.includes("anomaly") || text.includes("flow")) return NOTE_STYLES.activity;
	return NOTE_STYLES.info;
};

const getUserInitials = (name: string) => {
	if (!name) return "?";
	const parts = name.split(" ");
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
};

export const Note: FC<NoteProps> = ({ note, canDelete, onDelete }) => {
	const { icon: Icon, color } = getNoteStyle(note);
	const initials = getUserInitials(note.createdBy || "");
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const handleDelete = async () => {
		if (!onDelete) return;
		setDeleting(true);
		try {
			await onDelete(note.id);
		} finally {
			setDeleting(false);
			setConfirmingDelete(false);
		}
	};

	return (
		<div className="group relative flex gap-3">
			<div
				className={`relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${color} ring-2 ring-background`}
			>
				<Icon className="w-2.5 h-2.5 text-white" />
			</div>
			<div className="flex-1 min-w-0 -mt-0.5 relative">
				{confirmingDelete && (
					<div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/90 backdrop-blur-[1px]">
						<div className="flex items-center gap-2 text-xs">
							<span className="text-muted-foreground">Delete this note?</span>
							<button
								onClick={handleDelete}
								disabled={deleting}
								className="inline-flex items-center gap-1 rounded px-2 py-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
							>
								{deleting && <Loader2 className="w-3 h-3 animate-spin" />}
								Delete
							</button>
							<button
								onClick={() => setConfirmingDelete(false)}
								disabled={deleting}
								className="rounded px-2 py-1 hover:bg-accent text-muted-foreground disabled:opacity-50"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
				<div className="flex items-start gap-2">
					<div className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center text-[10px] font-medium shrink-0">
						{initials}
					</div>
					<div className="flex flex-col min-w-0">
						<span className="text-sm font-medium">{note.createdBy || "Unknown"}</span>
						<div className="flex items-center gap-1.5 mt-0.5">
							{note.organizationName && (
								<span className="inline-flex items-center rounded bg-secondary px-1.5 py-px text-[10px] font-medium text-muted-foreground">
									{note.organizationName}
								</span>
							)}
							<span className="text-[10px] font-mono text-muted-foreground/70">
								{format(new Date(note.createdAt), "MMM d, yyyy HH:mm")}
							</span>
						</div>
					</div>
				</div>
				{note.description && (
					<p className="text-sm text-muted-foreground leading-relaxed mt-1 line-clamp-2">
						{note.description}
					</p>
				)}
				<div className="mt-2">
					{note.mediaType === MEDIA_TYPES.IMAGE && <NoteImage {...note} />}
					{(note.mediaType === MEDIA_TYPES.VIDEO || note.mediaType === MEDIA_TYPES.AUDIO_VIDEO) && (
						<NoteVideo {...note} />
					)}
					{note.mediaType === MEDIA_TYPES.FILE && <NoteFile {...note} />}
					{![
						MEDIA_TYPES.TEXT,
						MEDIA_TYPES.IMAGE,
						MEDIA_TYPES.VIDEO,
						MEDIA_TYPES.AUDIO_VIDEO,
						MEDIA_TYPES.FILE,
					].includes(note.mediaType) && <NoteUnsupported type={MEDIA_TYPES[note.mediaType]} />}
				</div>
			</div>
			{canDelete && !confirmingDelete && (
				<button
					onClick={() => setConfirmingDelete(true)}
					className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-6 w-6 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
				>
					<Trash2 className="w-3.5 h-3.5" />
				</button>
			)}
		</div>
	);
};
